import { compareTwoStrings } from "string-similarity";
import AdvancedTransactionTypes from "const/AdvancedTransactionTypes";
import Constants from "const/Constants";
import ContactSubTypes from "const/ContactSubTypes";
import DataStatuses from "const/DataStatuses";
import IntegrationServices from "const/IntegrationServices";
import Texts from "utils/Texts";
import TransactionFieldsToEdit from "const/TransactionFieldsToEdit";
import TransactionTypes from "const/TransactionTypes";

export default class Transactions {
  static getTransactionState(transactionData) {
    const { address = {}, category = {} } = transactionData;

    const transactionState = {
      address,
      category,
      id: transactionData.id,
      manualMode: true,
      vendorId: transactionData.vendorId,
      class: transactionData.class,
      item: transactionData.item,
      location: transactionData.location,
      taxRate: transactionData.taxRate,
      project: transactionData.project,
      tags: transactionData.tags,
      reason: "",
      status: transactionData.status,
      usesItems: transactionData.usesItems,
      directCategorySelection: !!category.code
    };

    return transactionState;
  }

  static getTransactionAdvancedType(transactionData) {
    const { type, credit, extraData } = transactionData || {};

    if (extraData) {
      if (extraData.integrationService === IntegrationServices.XERO.value) {
        if (extraData.xeroBankTransactionId) {
          return type === TransactionTypes.WITHDRAW ? AdvancedTransactionTypes.SPENT : AdvancedTransactionTypes.RECEIVED;
        }
        if (extraData.xeroPaymentId) return AdvancedTransactionTypes.PAYMENT;

        return AdvancedTransactionTypes.BANK_FEED;
      } else if (extraData.integrationService === IntegrationServices.ZOHO.value) {
        return type === TransactionTypes.WITHDRAW ? AdvancedTransactionTypes.EXPENSE : AdvancedTransactionTypes.DEPOSIT;
      }
    }

    let advancedType = extraData
      && Texts.uncapitalize((extraData.quickBooksTransactionType || extraData.quickBooksDesktopTransactionType));

    if (advancedType === AdvancedTransactionTypes.PURCHASE) {
      advancedType = extraData.quickBooksPaymentType === "Check"
        ? AdvancedTransactionTypes.CHECK : AdvancedTransactionTypes.EXPENSE;
    } else if (advancedType === AdvancedTransactionTypes.PAYMENT) advancedType = AdvancedTransactionTypes.RECEIVED_PAYMENT;

    return advancedType
      ? (advancedType === AdvancedTransactionTypes.EXPENSE && credit ? AdvancedTransactionTypes.CREDIT_CARD_CREDIT : advancedType)
      : (type === TransactionTypes.WITHDRAW ? AdvancedTransactionTypes.EXPENSE : AdvancedTransactionTypes.INCOME);
  }

  static getChangedFields(state, original) {
    const changedFields = [
      state.reason && state.reason !== original.reason && [TransactionFieldsToEdit.REASON, state.reason],
      state.address?.name !== original.address?.name && [TransactionFieldsToEdit.ADDRESS, state.address],
      state.vendorId !== original.vendorId && [TransactionFieldsToEdit.VENDOR_ID, state.vendorId],
      state.category?.code !== original.category?.code && [TransactionFieldsToEdit.CATEGORY, state.category],
      state.class?.id !== original.class?.id && [TransactionFieldsToEdit.CLASS, state.class],
      state.project?.id !== original.project?.id && [TransactionFieldsToEdit.PROJECT, state.project],
      state.location?.id !== original.location?.id && [TransactionFieldsToEdit.LOCATION, state.location],
      state.taxRate?.id !== original.taxRate?.id && [TransactionFieldsToEdit.TAX_RATE, state.taxRate],
      state.item?.id !== original.item?.id && [TransactionFieldsToEdit.ITEM, state.item]
    ].filter(Boolean);

    return changedFields.length ? Object.fromEntries(changedFields) : null;
  }

  static validateContactChange(state, transaction) {
    const name = state?.address?.name;

    return !name || name === transaction?.address?.name || !!state.vendorId;
  }

  static validateToApprove(itemState = {}, transaction = {}, allowApproveWithoutPayee) {
    const { type, extraData } = transaction || {};

    const advancedType = type && extraData && Transactions.getTransactionAdvancedType({ type, extraData });

    const transferTransaction = advancedType === AdvancedTransactionTypes.TRANSFER;

    const {
      vendorId,
      category = {},
      item = {},
      location = {},
      project = {},
      taxRate = {},
      class: classValue = {}
    } = itemState;

    return !!(
      (vendorId || transferTransaction || allowApproveWithoutPayee)
        && (category.code || item.id)
        && (!location.name || location.id)
        && (!project.name || project.id)
        && (!taxRate.name || taxRate.id)
        && (!classValue.name || classValue.id)
    );
  }

  static checkIsReadyToReview(itemState, transaction, advancedDocumentsWorkflow) {
    const { reason, category = {} } = itemState || {};

    const { status, documentId, reason: originalReason } = transaction || {};

    return status === DataStatuses.NEED_REACTION && (
      (documentId && advancedDocumentsWorkflow)
      || (reason && reason.trim())
      || (category.code && !originalReason)
    );
  }

  static checkIsReadyToApprove(
    itemState = {},
    transaction = {},
    businessUser = false,
    advancedDocumentsWorkflow = false,
    allowApproveWithoutPayee = false
  ) {
    const { status } = itemState;

    return !businessUser
          && status !== DataStatuses.TO_REPORT
          && status !== DataStatuses.EXPORTED
          && (!advancedDocumentsWorkflow || !transaction.documentId)
          && Transactions.validateToApprove(itemState, transaction, allowApproveWithoutPayee);
  }

  static hasChanges(state, original) {
    return [
      state.reason && state.reason !== original.reason,
      state.address?.name !== original.address?.name,
      state.category?.code !== original.category?.code,
      state.class?.id !== original.class?.id,
      state.project?.id !== original.project?.id,
      state.location?.id !== original.location?.id,
      state.taxRate?.id !== original.taxRate?.id,
      state.item?.id !== original.item?.id
    ].some(Boolean);
  }

  static readyToReview(transaction) {
    const { status, documentId, reason, category = {} } = transaction || {};

    return status === DataStatuses.NEED_REACTION && (documentId || (reason && reason.trim()) || category.code);
  }

  static searchContact(contacts, id, name, usesItems) {
    let contactData = null;

    const foundItems = [];

    const nameLowerCased = name && name.trim().toLocaleLowerCase();

    contacts.some((contact) => {
      if (contact.id === id) {
        contactData = contact;

        return true;
      } else if (!usesItems || contact.subType !== ContactSubTypes.VENDOR) {
        if (nameLowerCased) {
          const contactNameLowerCased = contact.name && contact.name.trim().toLocaleLowerCase();

          if (nameLowerCased === contactNameLowerCased) {
            foundItems.push([1, contact]);
          } else {
            const similarity = compareTwoStrings(nameLowerCased, contactNameLowerCased);

            if (similarity > Constants.DATA_SIMILARITY_COEFFICIENT) {
              foundItems.push([similarity, contact]);
            }
          }
        }
      }

      return false;
    });

    return contactData || (
      foundItems.length && foundItems.sort(([similarityA], [similarityB]) => similarityB - similarityA)[0][1]
    ) || null;
  }
}
